import { createReducer } from '@reduxjs/toolkit';
import { bleActions } from './actions';
import { BleConnectionState, BleUpdateFirmwareState } from './constants';
import { BleState } from './types';

const bleReducerInitialState: BleState = {
  isLocked: false,
  connectableDevices: [],
  connectedDevice: undefined,
  lastConnectedDevice: undefined,
  connectionState: BleConnectionState.DISCONNECTED,
  updateFirmwareStatus: BleUpdateFirmwareState.UNDEFINED,
  remoteLogFiles: [],
  remoteBundleFiles: [],
  writeBundle: undefined,
  didPromptCreateController: false,
  isConnectControllerModalOpen: false,
};

export const bleReducer = createReducer(bleReducerInitialState, builder =>
  builder
    .addCase(bleActions.acknowledgeAndClearErrors, state => {
      state.lastConnectedDevice = undefined;
      state.updateFirmwareStatus = BleUpdateFirmwareState.UNDEFINED;
      if (state.connectionState === BleConnectionState.ERROR_CONNECTING)
        state.connectionState = BleConnectionState.DISCONNECTED;
    })
    .addCase(bleActions.updateSerialNumber, (state, action) => {
      if (state.connectedDevice) state.connectedDevice.serialNumber = action.payload;
    })
    .addCase(bleActions.updateFirmwareVersion, (state, action) => {
      if (state.connectedDevice) state.connectedDevice.firmwareVersion = action.payload;
    })
    .addCase(bleActions.scanDevicesStart, state => {
      state.isLocked = true;
      state.connectableDevices = [];
    })
    .addCase(bleActions.scanDevicesProgress, (state, action) => {
      // Can only update connectable devices while scan is in progress.
      if (state.isLocked) state.connectableDevices.push(action.payload.device);
    })
    .addCase(bleActions.scanDevicesSuccess, state => {
      state.isLocked = false;
    })
    .addCase(bleActions.scanDevicesFailure, state => {
      state.isLocked = false;
    })
    .addCase(bleActions.connectDeviceStart, state => {
      state.connectionState = BleConnectionState.ATTEMPTING_CONNECT;
      state.isLocked = true;
    })
    .addCase(bleActions.connectDeviceSuccess, (state, action) => {
      const firmwareVersion = action.payload.device.firmwareVersion;
      const formattedFirmwareVersion = firmwareVersion?.toLowerCase().startsWith('ctrl app v')
        ? firmwareVersion.substring(10)
        : firmwareVersion;

      const connectedDevice = {
        ...action.payload.device,
        firmwareVersion: formattedFirmwareVersion,
      };

      state.connectedDevice = connectedDevice;
      state.lastConnectedDevice = action.payload.device;
      state.connectionState = BleConnectionState.CONNECTED;
      state.isLocked = false;
      state.didPromptCreateController = false;
    })
    .addCase(bleActions.connectDeviceFailure, state => {
      state.connectionState = BleConnectionState.ERROR_CONNECTING;
      state.connectedDevice = undefined;
      state.isLocked = false;
    })
    .addCase(bleActions.disconnectDevice, state => {
      state.connectionState = BleConnectionState.DISCONNECTED;
      state.connectedDevice = undefined;
      state.connectableDevices = [];
      state.readLog = undefined;
      state.writeLog = undefined;
      state.writeBundle = undefined;
      state.remoteBundleFiles = [];
      state.didPromptCreateController = false;
    })
    .addCase(bleActions.deleteLogFileStart, state => {
      state.isLocked = true;
    })
    .addCase(bleActions.deleteLogFileSuccess, state => {
      state.isLocked = false;
    })
    .addCase(bleActions.deleteLogFileFailure, state => {
      state.isLocked = false;
    })
    .addCase(bleActions.readLogFileStart, (state, action) => {
      state.isLocked = true;
      state.readLog = {
        filename: action.payload.filename,
        totalBytes: 1,
        readBytes: 0,
      };
    })
    .addCase(bleActions.readLogFileProgress, (state, action) => {
      if (!state.readLog) return;
      state.readLog.readBytes = action.payload.readBytes;
      state.readLog.totalBytes = action.payload.totalBytes;
    })
    .addCase(bleActions.readLogFileSuccess, (state, action) => {
      state.isLocked = false;
      if (!state.readLog) return;
      state.readLog.data = action.payload.data;
      state.readLog.readBytes = state.readLog.totalBytes;
    })
    .addCase(bleActions.readLogFileFailure, state => {
      state.isLocked = false;
      state.readLog = undefined;
    })
    .addCase(bleActions.writeLogFileStart, (state, action) => {
      state.isLocked = true;
      state.writeLog = {
        writtenBytes: 0,
        totalBytes: action.payload.totalBytes,
      };
    })
    .addCase(bleActions.writeLogFileProgress, (state, action) => {
      if (!state.writeLog) return;
      state.writeLog.writtenBytes = action.payload.writtenBytes;
    })
    .addCase(bleActions.writeLogFileSuccess, state => {
      state.isLocked = false;
      state.writeLog = undefined;
    })
    .addCase(bleActions.writeLogFileFailure, state => {
      state.isLocked = false;
      state.writeLog = undefined;
    })
    .addCase(bleActions.listLogFilesStart, state => {
      state.isLocked = true;
    })
    .addCase(bleActions.listLogFilesSuccess, (state, action) => {
      state.isLocked = false;
      state.remoteLogFiles = action.payload.data;
    })
    .addCase(bleActions.listLogFilesFailure, state => {
      state.isLocked = false;
    })
    .addCase(bleActions.syncLogFileStart, (state, action) => {
      state.isLocked = true;
      state.syncLog = {
        filename: action.payload.filename,
        totalBytes: 1,
        syncedBytes: 0,
      };
    })
    .addCase(bleActions.syncLogFileProgress, (state, action) => {
      if (!state.syncLog) return;
      state.syncLog.syncedBytes = action.payload.uploadedBytes;
      state.syncLog.totalBytes = action.payload.totalBytes;
    })
    .addCase(bleActions.syncLogFileSuccess, state => {
      state.isLocked = false;
      if (!state.syncLog) return;
      state.syncLog.syncedBytes = state.syncLog.totalBytes;
      state.syncLog.hasErrors = false;
    })
    .addCase(bleActions.syncLogFileFailure, state => {
      state.isLocked = false;
      if (!state.syncLog) return;
      state.syncLog.hasErrors = true;
    })
    .addCase(bleActions.syncLogFileReset, state => {
      state.syncLog = undefined;
    })
    .addCase(bleActions.updateFirmwareStart, state => {
      state.isLocked = true;
    })
    .addCase(bleActions.updateFirmwareProgress, (state, action) => {
      state.updateFirmware = {
        writtenBytes: action.payload.writtenBytes,
        totalBytes: action.payload.totalBytes,
      };
    })
    .addCase(bleActions.updateFirmwareSuccess, state => {
      state.isLocked = false;
      state.updateFirmware = undefined;
      state.updateFirmwareStatus = BleUpdateFirmwareState.SUCCESS;
    })
    .addCase(bleActions.updateFirmwareFailure, (state, action) => {
      state.isLocked = false;
      state.updateFirmware = undefined;

      switch (action?.payload?.errorCode) {
        case 'fetchingFirmware':
          state.updateFirmwareStatus = BleUpdateFirmwareState.FAILURE_FETCHING;
          break;
        case 'assigningFirmwareLicense':
          state.updateFirmwareStatus = BleUpdateFirmwareState.FAILURE_LICENSING;
          break;
        case 'installingFirmware':
        default:
          state.updateFirmwareStatus = BleUpdateFirmwareState.FAILURE_INSTALLING;
          break;
      }
    })
    .addCase(bleActions.listBundlesStart, state => {
      state.isLocked = true;
    })
    .addCase(bleActions.listBundlesSuccess, (state, { payload }) => {
      state.isLocked = false;
      state.remoteBundleFiles = payload.data;
    })
    .addCase(bleActions.listBundlesFailure, state => {
      state.isLocked = false;
    })
    .addCase(bleActions.writeBundleStart, (state, { payload }) => {
      state.isLocked = true;
      state.writeBundle = {
        bundleId: payload.bundleId,
        totalBytes: payload.totalBytes,
        writtenBytes: 0,
      };
    })
    .addCase(bleActions.writeBundleProgress, (state, { payload }) => {
      if (!state.writeBundle) return;
      state.writeBundle.writtenBytes = payload.writtenBytes;
    })
    .addCase(bleActions.writeBundleSuccess, (state, { payload }) => {
      state.isLocked = false;
      state.writeBundle = undefined;
      state.remoteBundleFiles.push(payload.data);
    })
    .addCase(bleActions.writeBundleFailure, state => {
      state.isLocked = false;
      state.writeBundle = undefined;
    })
    .addCase(bleActions.deleteBundleStart, state => {
      state.isLocked = true;
    })
    .addCase(bleActions.deleteBundleFailure, state => {
      state.isLocked = false;
    })
    .addCase(bleActions.deleteBundleSuccess, (state, { payload }) => {
      state.isLocked = false;
      state.remoteBundleFiles = state.remoteBundleFiles.filter(i => i.bundleId !== payload.bundleId);
    })
    .addCase(bleActions.dismissCreateController, state => {
      state.didPromptCreateController = true;
    })
    .addCase(bleActions.openConnectControllerModal, state => {
      state.isConnectControllerModalOpen = true;
    })
    .addCase(bleActions.closeConnectControllerModal, state => {
      state.isConnectControllerModalOpen = false;
      state.connectableDevices = [];
    })
    .addDefaultCase(state => state),
);
